import React from 'react';
import { EnPowerWordmarkInline } from '../../../EnPowerLogo';
import { colors, fonts, space } from '../../tokens';
import { DraggableElement } from '../../../DraggableElement';
import { InlineEditableText } from '../../../InlineEditableText';

const SOLAR_IMAGE = '/solar-bg.jpg';

const sanitizeConfidentialNote = (note) => String(note || '').replace('EN:POWER GmbH', 'EN:POWER');

const renderConfidentialNote = (note) => {
  const text = sanitizeConfidentialNote(note);
  const brandIndex = text.indexOf('EN:POWER');

  if (brandIndex === -1) {
    return text;
  }

  return (
    <>
      {text.slice(0, brandIndex)}
      <EnPowerWordmarkInline enColor="#FFFFFF" fontSize="7px" />
      {text.slice(brandIndex + 'EN:POWER'.length)}
    </>
  );
};

/**
 * HeroSection – Deckblatt (Page 1) des Dach-PV-Angebots.
 * Enthält Branding, HeroImage, Eyebrow, MainHeadline, Subheadline und MetaBar.
 */
export const HeroSection = ({
  kundenname,
  kundenadresse,
  angebotsnummer,
  kundennummer,
  datum,
  gueltigBis,
  content = {},
  onTextChange,
  editMode = false,
  layoutEditLevel = 'section',
  dragProps = {},
}) => {
  const coverText = {
    badge: content?.badge || 'Indikationsangebot',
    eyebrow: content?.eyebrow || 'Photovoltaik • Dachverpachtung',
    headline: content?.headline || 'IHR\nANGEBOT',
    sublinePrefix: content?.sublinePrefix || 'Exklusiv erstellt für',
    projectAddressLabel: content?.projectAddressLabel || 'Projektadresse',
    confidentialNote: sanitizeConfidentialNote(
      content?.confidentialNote ||
      'Dieses Angebot ist vertraulich und wurde ausschließlich für den genannten Empfänger erstellt. · EN:POWER · info@enpower.de'
    ),
  };

  const metaItems = [
    { label: 'Angebots-Nr.', value: angebotsnummer },
    { label: 'Kunden-Nr.', value: kundennummer },
    { label: 'Datum', value: datum },
    { label: 'Gültig bis', value: gueltigBis, highlight: true },
  ];

  const horizontalPadding = `${space.xl + space.sm}px`;
  const titleBlockStyle = {
    position: 'absolute',
    top: '31.8%',
    left: horizontalPadding,
    right: horizontalPadding,
  };

  const headerRow = (
    <div style={{ position: 'absolute', top: '34px', left: horizontalPadding, right: horizontalPadding, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <EnPowerWordmarkInline
        enColor="#FFFFFF"
        powerColor="#E07000"
        fontFamily="Outfit, sans-serif"
        fontSize="32px"
        fontWeight={800}
        letterSpacing="-0.02em"
      />
      <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '4px', padding: '5px 14px', fontFamily: fonts.body, fontSize: '8.5px', fontWeight: 700, color: 'rgba(255,255,255,0.8)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
        <InlineEditableText
          as="span"
          value={coverText.badge}
          editable={editMode}
          onChange={(value) => onTextChange?.('cover.badge', value)}
        />
      </div>
    </div>
  );

  const titleBlock = (
    <div style={titleBlockStyle}>
      <InlineEditableText
        as="p"
        value={coverText.eyebrow}
        editable={editMode}
        onChange={(value) => onTextChange?.('cover.eyebrow', value)}
        style={{ fontFamily: fonts.body, fontSize: '9px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: colors.orange, marginBottom: '12px' }}
      />
      <InlineEditableText
        as="h1"
        value={coverText.headline}
        editable={editMode}
        multiline
        onChange={(value) => onTextChange?.('cover.headline', value)}
        style={{ fontFamily: fonts.display, fontWeight: 800, fontSize: '68px', color: 'white', letterSpacing: '-0.04em', lineHeight: 0.9, marginBottom: '18px', textShadow: '0 2px 24px rgba(0,0,0,0.5)', whiteSpace: 'pre-line' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ width: '52px', height: '3px', background: colors.orange, borderRadius: '2px', flexShrink: 0 }} />
        <p style={{ fontFamily: fonts.body, fontSize: '13.5px', fontWeight: 500, color: 'rgba(255,255,255,0.75)', margin: 0 }}>
          <InlineEditableText
            as="span"
            value={coverText.sublinePrefix}
            editable={editMode}
            onChange={(value) => onTextChange?.('cover.sublinePrefix', value)}
          />{' '}
          <span style={{ color: 'white', fontWeight: 700 }}>{kundenname}</span>
        </p>
      </div>
    </div>
  );

  const metaBlock = (
    <div style={{ position: 'absolute', left: horizontalPadding, right: horizontalPadding, bottom: '36px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', marginTop: '24px', padding: '8px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '7px' }}>
        <InlineEditableText
          as="span"
          value={coverText.projectAddressLabel}
          editable={editMode}
          onChange={(value) => onTextChange?.('cover.projectAddressLabel', value)}
          style={{ fontFamily: fonts.body, fontSize: '8.5px', fontWeight: 700, color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: '0.08em' }}
        />
        <div style={{ width: '1px', height: '12px', background: 'rgba(255,255,255,0.15)' }} />
        <span style={{ fontFamily: fonts.body, fontSize: '10.5px', fontWeight: 500, color: 'rgba(255,255,255,0.92)' }}>{kundenadresse}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingTop: '18px', paddingBottom: '18px' }}>
        {metaItems.map((item, i) => (
          <div key={i} style={{ padding: '0 16px', borderRight: i < metaItems.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
            <p style={{ fontFamily: fonts.body, fontSize: '8px', fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '5px' }}>{item.label}</p>
            <p style={{ fontFamily: fonts.display, fontSize: '13px', fontWeight: 600, color: item.highlight ? colors.orange : 'white', letterSpacing: '-0.01em', margin: 0 }}>{item.value}</p>
          </div>
        ))}
      </div>

      {editMode ? (
        <InlineEditableText
          as="p"
          value={coverText.confidentialNote}
          editable={editMode}
          multiline
          onChange={(value) => onTextChange?.('cover.confidentialNote', value)}
          style={{ fontFamily: fonts.body, fontSize: '8px', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.03em', marginTop: '14px' }}
        />
      ) : (
        <p style={{ fontFamily: fonts.body, fontSize: '8px', color: 'rgba(255,255,255,0.25)', letterSpacing: '0.03em', marginTop: '14px' }}>
          {renderConfidentialNote(coverText.confidentialNote)}
        </p>
      )}
    </div>
  );

  return (
    <>
      {/* HeroImage layer */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '52%', overflow: 'hidden' }}>
        <img
          src={SOLAR_IMAGE}
          alt="Gewerbliche Dachphotovoltaik-Anlage"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block', filter: 'brightness(0.78) saturate(0.9)' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(14,30,60,0.18) 0%, transparent 25%, rgba(14,30,60,0.65) 72%, #0E1E3C 100%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '98px', background: 'linear-gradient(to bottom, #0E1E3C 0%, rgba(14,30,60,0.58) 66px, transparent 100%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: 'linear-gradient(to bottom, #E07000, rgba(224,112,0,0.2))' }} />
      </div>

      {/* Navy bottom half */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '52%', background: 'linear-gradient(160deg, #122A5C 0%, #0E1E3C 100%)' }} />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 10, height: '100%' }}>
        {layoutEditLevel === 'detail' ? (
          <div style={{ position: 'absolute', inset: 0 }}>
            <DraggableElement id="S1-Deckblatt-Inhalt" {...dragProps} dragAxis="y" initialStyle={{ width: '100%', height: '100%' }}>
              {headerRow}
              {titleBlock}
            </DraggableElement>
          </div>
        ) : (
          <>
            {headerRow}
            {titleBlock}
          </>
        )}

        {layoutEditLevel === 'detail' ? (
          <div style={{ position: 'absolute', inset: 0 }}>
            <DraggableElement id="S1-Deckblatt-Meta" {...dragProps} dragAxis="y" initialStyle={{ width: '100%', height: '100%' }}>
              {metaBlock}
            </DraggableElement>
          </div>
        ) : (
          metaBlock
        )}
      </div>
    </>
  );
};
